import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { TeamService } from '../../services/teamService';
import { supabase } from '../../services/supabaseClient';
import { showToast } from '../../utils/toast';
import type { Team, TeamMember } from '../../types/team';
import '../../styles/components/teams/TeamManagementModal.css';

interface TeamManagementModalProps {
    team: Team;
    onClose: () => void;
    onTeamUpdate: () => void;
}

const TeamManagementModal: React.FC<TeamManagementModalProps> = ({
    team,
    onClose,
    onTeamUpdate
}) => {
    const { user } = useAuth();
    const { t } = useLanguage();
    const [activeTab, setActiveTab] = useState<'overview' | 'members' | 'settings'>('overview');
    const [loading, setLoading] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState<{
        type: 'leave' | 'remove' | 'disband';
        member?: TeamMember;
        reason?: string;
    } | null>(null);
    const [removalReason, setRemovalReason] = useState('');
    const [disbandReason, setDisbandReason] = useState('');

    const isOwner = user?.id === team.owner_id;
    const currentUserMember = team.members?.find(m => m.user_id === user?.id);

    const handleLeaveTeam = async () => {
        if (!user || !currentUserMember) return;

        setLoading(true);
        try {
            // Check if user is the owner
            if (team.owner_id === user.id) {
                showToast(t('teams.ownerCannotLeave'), 'warning');
                setLoading(false);
                return;
            }
            
            // Confirm the action
            if (!window.confirm(t('teams.confirmLeaveTeam', { teamName: team.name }))) {
                setLoading(false);
                return;
            }
            
            // Simple direct update approach - just update is_active
            try {
                // First, verify the team member record exists and is active
                const { data: memberCheck, error: checkError } = await supabase
                    .from('team_members')
                    .select('*')
                    .eq('team_id', team.id)
                    .eq('user_id', user.id)
                    .eq('is_active', true);
                
                if (checkError) {
                    throw checkError;
                }
                
                if (!memberCheck || memberCheck.length === 0) {
                    throw new Error('No active team membership found');
                }
                
                // Try using the RPC function to leave the team
                
                try {
                    // Call the leave_team_rpc function
                    const { data: rpcResult, error: rpcError } = await supabase
                        .rpc('leave_team_rpc', {
                            p_team_id: team.id,
                            p_user_id: user.id
                        });
                    
                    if (rpcError) {
                        throw rpcError;
                    }
                    
                    // Check if the RPC function was successful
                    if (rpcResult && typeof rpcResult === 'object' && !rpcResult.success) {
                        throw new Error(rpcResult.message || 'Failed to leave team');
                    }
                    
                    // If RPC result is just a boolean true, that's also success
                    if (rpcResult === false) {
                        throw new Error('RPC function returned false - update failed');
                    }
                    
                } catch (rpcException) {
                    // Fall back to standard update
                    const { data: stdUpdateData, error: stdUpdateError } = await supabase
                        .from('team_members')
                        .update({ is_active: false })
                        .eq('team_id', team.id)
                        .eq('user_id', user.id)
                        .select();
                    
                    if (stdUpdateError) {
                        throw stdUpdateError;
                    }
                    
                    // Check if the standard update actually updated any rows
                    if (!stdUpdateData || stdUpdateData.length === 0) {
                        throw new Error('No rows were updated - team membership may not exist or already inactive');
                    }
                }
                
                // No need to check for errors here - we've already handled them above
                
                // Verify the update was successful
                const { data: verifyData, error: verifyError } = await supabase
                    .from('team_members')
                    .select('is_active')
                    .eq('team_id', team.id)
                    .eq('user_id', user.id);
                
                if (verifyError) {
                    // Silently handle verification errors
                } else if (!verifyData || verifyData.length === 0 || verifyData[0].is_active !== false) {
                    // Verification failed but we'll continue anyway
                }
                showToast(t('teams.leftTeamSuccess', { teamName: team.name }), 'success');
                
                // Force a complete refresh to ensure UI is updated
                setTimeout(() => {
                    onTeamUpdate();
                    onClose();
                    
                    // Force a page reload to ensure the UI is completely refreshed
                    window.location.reload();
                }, 500);
            } catch (updateError) {
                // Try using the TeamService as fallback
                await TeamService.leaveTeam(team.id, user.id);
                
                showToast(t('teams.leftTeamSuccess', { teamName: team.name }), 'success');
                onTeamUpdate();
                onClose();
            }
        } catch (error) {
            // Display a more helpful error message
            const errorMessage = error instanceof Error ? error.message : t('teams.failedToRemoveMember');
            showToast(t('teams.errorLeaveTeam', { message: errorMessage }), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveMember = async (member: TeamMember, reason?: string) => {
        if (!user || !isOwner) return;

        setLoading(true);
        try {
            await TeamService.removeTeamMember(team.id, member.user_id, user.id, reason);
            onTeamUpdate();
            setShowConfirmDialog(null);
        } catch (error) {
            showToast(t('teams.failedToRemoveMember'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDisbandTeam = async (reason?: string) => {
        if (!user || !isOwner) return;

        setLoading(true);
        try {
            await TeamService.disbandTeam(team.id, user.id, reason);
            onTeamUpdate();
            onClose();
        } catch (error) {
            showToast(t('teams.failedToDisbandTeam'), 'error');
        } finally {
            setLoading(false);
        }
    };

    const formatTimeAgo = (timestamp: string) => {
        const now = new Date();
        const time = new Date(timestamp);
        const diffMs = now.getTime() - time.getTime();
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffDays === 0) return t('teams.today');
        if (diffDays === 1) return t('teams.yesterday');
        if (diffDays < 30) return t('teams.daysAgo', { days: diffDays });
        return time.toLocaleDateString();
    };

    const renderOverviewTab = () => (
        <div className="tab-content">
            <div className="team-overview">
                <div className="team-header">
                    <h3>{team.name}</h3>
                    <div className="team-status">
                        <span className={`status-badge ${team.status}`}>
                            {team.status === 'active' ? t('teams.statusActive') : t('teams.statusInactive')}
                        </span>
                    </div>
                </div>

                {team.description && (
                    <div className="team-description">
                        <h4>{t('teams.descriptionHeading')}</h4>
                        <p>{team.description}</p>
                    </div>
                )}

                <div className="team-stats">
                    <div className="stat-item">
                        <span className="stat-label">{t('teams.membersCount')}</span>
                        <span className="stat-value">{team.members_count}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">{t('teams.createdLabel')}</span>
                        <span className="stat-value">{formatTimeAgo(team.created_at)}</span>
                    </div>
                    <div className="stat-item">
                        <span className="stat-label">{t('teams.ownerLabel')}</span>
                        <span className="stat-value">
                            {team.owner?.display_name || team.owner?.username}
                        </span>
                    </div>
                </div>

                {team.project_details && (
                    <div className="project-details">
                        <h4>{t('teams.projectDetailsHeading')}</h4>
                        <p>{team.project_details}</p>
                    </div>
                )}
            </div>
        </div>
    );

    const renderMembersTab = () => (
        <div className="tab-content">
            <div className="members-section">
                <div className="members-header">
                    <h4>{t('teams.teamMembersCount', { count: team.members_count })}</h4>
                    {isOwner && team.status === 'active' && (
                        <button className="btn-secondary btn-small">
                            <i className="bi bi-person-plus"></i>
                            {t('teams.inviteMembers')}
                        </button>
                    )}
                </div>

                <div className="members-list">
                    {team.members?.map((member) => (
                        <div key={member.id} className="member-item">
                            <div className="member-info">
                                <div className="member-avatar">
                                    {member.user?.avatar_url ? (
                                        <img src={member.user.avatar_url} alt={member.user?.username} />
                                    ) : (
                                        <div className="avatar-placeholder">
                                            {member.user?.display_name?.[0] || member.user?.username?.[0] || '?'}
                                        </div>
                                    )}
                                </div>
                                <div className="member-details">
                                    <div className="member-name">
                                        {member.user?.display_name || member.user?.username}
                                    </div>
                                    <div className="member-role">
                                        {member.role === 'owner' ? t('teams.roleOwner') : member.role === 'member' ? t('teams.roleMember') : member.role}
                                        {member.user_id === team.owner_id && (
                                            <span className="owner-badge">{t('teams.ownerBadge')}</span>
                                        )}
                                    </div>
                                    <div className="member-joined">
                                        {t('teams.joinedLabel')} {formatTimeAgo(member.joined_at)}
                                    </div>
                                </div>
                            </div>

                            <div className="member-actions">
                                {member.is_active ? (
                                    <span className="status-active">{t('teams.activeStatus')}</span>
                                ) : (
                                    <span className="status-inactive">{t('teams.inactiveStatus')}</span>
                                )}

                                {isOwner && member.user_id !== team.owner_id && member.is_active && (
                                    <button
                                        className="btn-danger btn-small"
                                        onClick={() => setShowConfirmDialog({
                                            type: 'remove',
                                            member
                                        })}
                                    >
                                        <i className="bi bi-person-x"></i>
                                        {t('teams.removeButton')}
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderSettingsTab = () => (
        <div className="tab-content">
            <div className="settings-section">
                <h4>{t('teams.teamSettingsHeading')}</h4>

                <div className="settings-group">
                    <h5>{t('teams.memberActionsHeading')}</h5>

                    {currentUserMember && currentUserMember.is_active && (
                        <div className="setting-item">
                            <div className="setting-info">
                                <span className="setting-title">{t('teams.leaveTeamTitle')}</span>
                                <span className="setting-description">
                                    {t('teams.leaveTeamDescription')}
                                </span>
                            </div>
                            <button
                                className="btn-warning"
                                onClick={() => setShowConfirmDialog({ type: 'leave' })}
                                disabled={loading}
                            >
                                <i className="bi bi-box-arrow-right"></i>
                                {t('teams.leaveTeamButton')}
                            </button>
                        </div>
                    )}
                </div>

                {isOwner && (
                    <div className="settings-group danger-zone">
                        <h5>{t('teams.dangerZone')}</h5>

                        <div className="setting-item">
                            <div className="setting-info">
                                <span className="setting-title">{t('teams.disbandTeamTitle')}</span>
                                <span className="setting-description">
                                    {t('teams.disbandTeamDescription')}
                                </span>
                            </div>
                            <button
                                className="btn-danger"
                                onClick={() => setShowConfirmDialog({ type: 'disband' })}
                                disabled={loading}
                            >
                                <i className="bi bi-trash"></i>
                                {t('teams.disbandTeamButton')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const renderConfirmDialog = () => {
        if (!showConfirmDialog) return null;

        const { type, member } = showConfirmDialog;

        return (
            <div className="confirm-dialog-overlay">
                <div className="confirm-dialog">
                    <div className="dialog-header">
                        <h4>
                            {type === 'leave' && t('teams.leaveTeamDialogTitle')}
                            {type === 'remove' && t('teams.removeMemberDialogTitle')}
                            {type === 'disband' && t('teams.disbandTeamDialogTitle')}
                        </h4>
                        <button
                            className="close-btn"
                            onClick={() => setShowConfirmDialog(null)}
                        >
                            <i className="bi bi-x"></i>
                        </button>
                    </div>

                    <div className="dialog-content">
                        {type === 'leave' && (
                            <div>
                                <p>{t('teams.leaveTeamConfirm', { teamName: team.name })}</p>
                                <p className="warning-text">
                                    {t('teams.leaveTeamWarning')}
                                </p>
                            </div>
                        )}

                        {type === 'remove' && member && (
                            <div>
                                <p>
                                    {t('teams.removeMemberConfirm', { 
                                        memberName: member.user?.display_name || member.user?.username,
                                        teamName: team.name 
                                    })}
                                </p>
                                <div className="reason-input">
                                    <label htmlFor="removal-reason">{t('teams.removalReasonLabel')}</label>
                                    <textarea
                                        id="removal-reason"
                                        value={removalReason}
                                        onChange={(e) => setRemovalReason(e.target.value)}
                                        placeholder={t('teams.removalReasonPlaceholder')}
                                        rows={3}
                                    />
                                </div>
                            </div>
                        )}

                        {type === 'disband' && (
                            <div>
                                <p>{t('teams.disbandTeamConfirm', { teamName: team.name })}</p>
                                <p className="warning-text">
                                    {t('teams.disbandTeamWarning')}
                                </p>
                                <div className="reason-input">
                                    <label htmlFor="disband-reason">{t('teams.disbandReasonLabel')}</label>
                                    <textarea
                                        id="disband-reason"
                                        value={disbandReason}
                                        onChange={(e) => setDisbandReason(e.target.value)}
                                        placeholder={t('teams.disbandReasonPlaceholder')}
                                        rows={3}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="dialog-actions">
                        <button
                            className="btn-secondary"
                            onClick={() => setShowConfirmDialog(null)}
                            disabled={loading}
                        >
                            {t('teams.cancelButton')}
                        </button>
                        <button
                            className={`btn-${type === 'leave' ? 'warning' : 'danger'}`}
                            onClick={() => {
                                if (type === 'leave') {
                                    handleLeaveTeam();
                                } else if (type === 'remove' && member) {
                                    handleRemoveMember(member, removalReason || undefined);
                                } else if (type === 'disband') {
                                    handleDisbandTeam(disbandReason || undefined);
                                }
                            }}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <div className="spinner"></div>
                                    {t('teams.processing')}
                                </>
                            ) : (
                                <>
                                    {type === 'leave' && t('teams.leaveTeamButton')}
                                    {type === 'remove' && t('teams.removeButton')}
                                    {type === 'disband' && t('teams.disbandTeamButton')}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="team-management-modal">
                <div className="modal-header">
                    <h3>{t('teams.teamManagementTitle')}</h3>
                    <button className="close-btn" onClick={onClose}>
                        <i className="bi bi-x"></i>
                    </button>
                </div>

                <div className="modal-tabs">
                    <button
                        className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        <i className="bi bi-info-circle"></i>
                        {t('teams.overviewTab')}
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'members' ? 'active' : ''}`}
                        onClick={() => setActiveTab('members')}
                    >
                        <i className="bi bi-people"></i>
                        {t('teams.membersTab')}
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
                        onClick={() => setActiveTab('settings')}
                    >
                        <i className="bi bi-gear"></i>
                        {t('teams.settingsTab')}
                    </button>
                </div>

                <div className="modal-body">
                    {activeTab === 'overview' && renderOverviewTab()}
                    {activeTab === 'members' && renderMembersTab()}
                    {activeTab === 'settings' && renderSettingsTab()}
                </div>

                {renderConfirmDialog()}
            </div>
        </div>
    );
};

export default TeamManagementModal;